<template>
  <div class="dashboard-shell">
    <div class="overlay" :class="{ active: !sidebarCollapsed }" @click="toggleSidebar"></div>

    <nav class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="brand" v-show="!sidebarCollapsed">
          <img :src="sidebarLogoSrc" alt="JMA Sysc" class="brand-logo">
        </div>

        <button v-if="sidebarCollapsed" @click="toggleSidebar" class="toggle-btn" aria-label="Abrir sidebar">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

      <ul class="nav-menu">
        <li>
          <router-link to="/dashboard" class="nav-link" exact-active-class="active" @click="closeSidebar">
            <i class="fas fa-home"></i>
            <span v-show="!sidebarCollapsed">Início</span>
          </router-link>
        </li>
        <li>
          <router-link to="/assistente" class="nav-link" active-class="active" @click="closeSidebar">
            <i class="fas fa-robot"></i>
            <span v-show="!sidebarCollapsed">Assistente IA</span>
          </router-link>
        </li>
        <li>
          <router-link to="/dashboard/clientes" class="nav-link" active-class="active" @click="closeSidebar">
            <i class="fas fa-users"></i>
            <span v-show="!sidebarCollapsed">Clientes</span>
          </router-link>
        </li>
        <li>
          <router-link to="/dashboard/honorarios" class="nav-link" active-class="active" @click="closeSidebar">
            <i class="fas fa-money-bill-wave"></i>
            <span v-show="!sidebarCollapsed">Honorários</span>
          </router-link>
        </li>
        <li>
          <router-link to="/dashboard/perfil" class="nav-link" active-class="active" @click="closeSidebar">
            <i class="fas fa-user-cog"></i>
            <span v-show="!sidebarCollapsed">Perfil</span>
          </router-link>
        </li>
      </ul>

      <div class="sidebar-footer">
        <div class="user-panel" :class="{ open: userMenuOpen, compact: sidebarCollapsed }">
          <button class="user-trigger" @click="toggleUserMenu">
            <span v-if="currentUser?.photoUrl || currentUser?.avatar" class="user-avatar user-avatar-image">
              <img :src="currentUser?.photoUrl || currentUser?.avatar || ''" alt="Avatar do usuario">
            </span>
            <span v-else class="user-avatar">
              {{ userInitials }}
            </span>

            <div v-show="!sidebarCollapsed" class="user-copy">
              <strong>{{ userDisplayName }}</strong>
              <span>{{ currentUser?.email || 'Sem email' }}</span>
            </div>

            <i v-show="!sidebarCollapsed" class="fas fa-chevron-up user-chevron"></i>
          </button>

          <div v-if="userMenuOpen" class="user-popover">
            <p class="user-popover-title">Conta</p>
            <div class="user-popover-item">
              <span>Email</span>
              <strong>{{ currentUser?.email || '-' }}</strong>
            </div>
            <div class="user-popover-item">
              <span>User</span>
              <strong>{{ currentUser?.displayName || '-' }}</strong>
            </div>
            <div class="user-popover-item">
              <span>User da empresa</span>
              <strong>{{ currentUser?.userId || '-' }}</strong>
            </div>
            <button class="profile-shortcut" @click="goToProfile">
              <i class="fas fa-user-cog"></i>
              <span>Editar Perfil</span>
            </button>
          </div>
        </div>

        <div class="footer-buttons">
          <button @click="toggleTheme" class="theme-btn">
            <i :class="isDarkTheme ? 'fas fa-sun' : 'fas fa-moon'"></i>
            <span v-show="!sidebarCollapsed">Tema</span>
          </button>
          <button @click="logout" class="logout-btn">
            <i class="fas fa-sign-out-alt"></i>
            <span v-show="!sidebarCollapsed">Sair</span>
          </button>
        </div>
      </div>
    </nav>

    <main class="main-content">
      <img :src="dashboardArtSrc" alt="" class="dashboard-art" aria-hidden="true">

      <header class="content-header">
        <div class="content-title-wrap">
          <button v-if="sidebarCollapsed" type="button" class="mobile-menu-btn" @click="toggleSidebar" aria-label="Abrir menu">
            <i class="fas fa-chevron-right"></i>
          </button>
          <p class="eyebrow">Painel</p>
          <h1 :class="{ 'title-regular': isHonorariosTitle }">{{ pageTitle }}</h1>
        </div>
      </header>

      <section class="content-body">
        <router-view />
      </section>
    </main>
  </div>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import type { AuthUser } from '@/types'

export default {
  name: 'Dashboard',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const sidebarCollapsed = ref(true)
    const isMobileViewport = ref(false)
    const userMenuOpen = ref(false)
    const currentUser = computed<AuthUser | null>(() => store.state.currentUser)
    const isDarkTheme = computed(() => store.state.isDarkTheme)
    const sidebarLogoSrc = computed(() => isDarkTheme.value ? '/jmasysc.ltr.png' : '/jmasysc.ltrbr.png')
    const dashboardArtSrc = computed(() => isDarkTheme.value ? '/damabranca.png' : '/dama.png')
    const isHonorariosTitle = computed(() => route.name === 'Honorarios')
    const userDisplayName = computed(() =>
      currentUser.value?.displayName || currentUser.value?.companyName || 'Usuario'
    )
    const userInitials = computed(() => {
      const source = userDisplayName.value || currentUser.value?.email || 'U'
      return source
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('')
    })
    const pageTitle = computed(() => {
      const titleMap: Record<string, string> = {
        Home: 'Visão Geral',
        Clientes: 'Gestão de Clientes',
        Honorarios: 'Gestão de Honorários'
      }
      titleMap.Perfil = 'Editar Perfil'
      return titleMap[String(route.name || '')] || 'Dashboard'
    })

    const syncViewportState = () => {
      if (typeof window === 'undefined') {
        return
      }

      isMobileViewport.value = window.innerWidth <= 900

      sidebarCollapsed.value = true
    }

    const toggleSidebar = () => {
      sidebarCollapsed.value = !sidebarCollapsed.value
      if (sidebarCollapsed.value) {
        userMenuOpen.value = false
      }
    }

    const toggleTheme = () => {
      store.commit('TOGGLE_THEME')
    }

    const toggleUserMenu = () => {
      userMenuOpen.value = !userMenuOpen.value
    }

    const goToProfile = () => {
      userMenuOpen.value = false
      router.push('/dashboard/perfil')
      closeSidebar()
    }

    const logout = () => {
      store.dispatch('logout')
      router.push('/')
    }

    const closeSidebar = () => {
      sidebarCollapsed.value = true
      userMenuOpen.value = false
    }

    onMounted(() => {
      syncViewportState()
      window.addEventListener('resize', syncViewportState)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', syncViewportState)
    })

    return {
      sidebarCollapsed,
      isMobileViewport,
      userMenuOpen,
      currentUser,
      userDisplayName,
      userInitials,
      isDarkTheme,
      sidebarLogoSrc,
      dashboardArtSrc,
      isHonorariosTitle,
      pageTitle,
      toggleSidebar,
      toggleTheme,
      toggleUserMenu,
      goToProfile,
      logout,
      closeSidebar
    }
  }
}
</script>

<style scoped>
.dashboard-shell {
  display: flex;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 14% 12%, rgba(42, 91, 255, 0.16), transparent 40%),
    radial-gradient(circle at 88% 82%, rgba(9, 151, 227, 0.14), transparent 38%),
    var(--bg-primary);
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.overlay.active {
  opacity: 1;
  visibility: visible;
}

.sidebar {
  width: 264px;
  height: 100vh;
  background: color-mix(in srgb, var(--bg-secondary) 88%, transparent);
  border-right: 1px solid color-mix(in srgb, var(--border-color) 78%, transparent);
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease;
  position: sticky;
  top: 0;
  align-self: flex-start;
  z-index: 1000;
  backdrop-filter: blur(10px);
  overflow: hidden;
}

.sidebar.collapsed {
  width: 86px;
}

.sidebar-header {
  padding: 18px 16px;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color) 76%, transparent);
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 122px;
  position: relative;
}

.sidebar.collapsed .sidebar-header {
  justify-content: center;
  padding: 14px;
}

.brand {
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-logo {
  width: 196px;
  max-height: 116px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.22));
}

.footer-buttons {
  display: flex;
  gap: 10px;
}

.theme-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
  color: var(--text-secondary);
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  font-size: 12px;
  font-weight: 500;
}

.theme-btn:hover {
  background-color: color-mix(in srgb, var(--bg-primary) 75%, transparent);
  color: var(--text-primary);
}

.theme-btn i {
  margin-right: 5px;
}

.sidebar.collapsed .theme-btn i {
  margin-right: 0;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  width: 100%;
  height: 44px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover {
  color: var(--text-primary);
  background: color-mix(in srgb, var(--bg-primary) 78%, transparent);
}

.nav-menu {
  flex: 1;
  list-style: none;
  padding: 14px 10px;
  overflow-y: auto;
  min-height: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  padding: 11px 14px;
  border-radius: 12px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.nav-link:hover {
  background: color-mix(in srgb, var(--bg-primary) 78%, transparent);
  color: var(--text-primary);
}

.nav-link.active {
  background: linear-gradient(120deg, rgba(57, 121, 255, 0.27), rgba(0, 183, 255, 0.16));
  color: var(--text-primary);
  border: 1px solid rgba(89, 164, 255, 0.35);
}

.nav-link i {
  width: 18px;
  text-align: center;
}

.sidebar.collapsed .nav-link {
  justify-content: center;
  padding: 12px;
}

.sidebar.collapsed .nav-link i {
  margin-right: 0;
}

.sidebar-footer {
  margin-top: auto;
  z-index: 2;
  padding: 14px;
  border-top: 1px solid color-mix(in srgb, var(--border-color) 76%, transparent);
  background: color-mix(in srgb, var(--bg-secondary) 94%, var(--bg-primary));
  backdrop-filter: blur(12px);
  box-shadow: 0 -10px 28px rgba(0, 0, 0, 0.12);
}

.user-panel {
  position: relative;
  margin-bottom: 12px;
}

.user-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
  background: color-mix(in srgb, var(--bg-primary) 38%, transparent);
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.user-trigger:hover {
  background: color-mix(in srgb, var(--bg-primary) 70%, transparent);
}

.user-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(87, 146, 255, 0.92), rgba(31, 208, 255, 0.72));
  color: white;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}

.user-avatar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-copy {
  min-width: 0;
  display: grid;
  gap: 2px;
  text-align: left;
  flex: 1;
}

.user-copy strong,
.user-popover-item strong {
  color: var(--text-primary);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-copy span,
.user-popover-item span,
.user-popover-title {
  color: var(--text-secondary);
  font-size: 12px;
}

.user-chevron {
  margin-left: auto;
  transition: transform 0.2s ease;
}

.user-panel.open .user-chevron {
  transform: rotate(180deg);
}

.user-popover {
  margin-top: 10px;
  padding: 14px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--bg-secondary) 92%, var(--bg-primary));
  border: 1px solid color-mix(in srgb, var(--border-color) 82%, transparent);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
  display: grid;
  gap: 10px;
}

.user-popover-title {
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.user-popover-item {
  display: grid;
  gap: 3px;
}

.profile-shortcut {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
  background: color-mix(in srgb, rgba(57, 121, 255, 0.24) 55%, transparent);
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  font-size: 13px;
  font-weight: 600;
}

.profile-shortcut:hover {
  background: color-mix(in srgb, rgba(57, 121, 255, 0.34) 65%, transparent);
}

.user-panel.compact .user-trigger {
  justify-content: center;
  padding: 10px;
}

.user-panel.compact .user-popover {
  position: absolute;
  left: calc(100% + 12px);
  bottom: 0;
  width: 240px;
  margin-top: 0;
}

.logout-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  background: none;
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
  color: var(--text-secondary);
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  font-size: 12px;
  font-weight: 500;
}

.logout-btn:hover {
  background: rgba(231, 76, 60, 0.12);
  color: #ff8d83;
  border-color: rgba(231, 76, 60, 0.46);
}

.logout-btn i {
  margin-right: 5px;
}

.sidebar.collapsed .logout-btn i {
  margin-right: 0;
}

.sidebar.collapsed .footer-buttons {
  flex-direction: column;
  gap: 8px;
}

.sidebar.collapsed .sidebar-footer {
  padding: 12px 10px;
}

.main-content {
  flex: 1;
  min-width: 0;
  padding: 28px;
  background: transparent;
  overflow-y: auto;
  position: relative;
}

.content-title-wrap {
  display: grid;
  gap: 6px;
}

.mobile-menu-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--bg-secondary) 94%, var(--bg-primary));
  color: var(--text-primary);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.dashboard-art {
  position: fixed;
  right: clamp(8px, 2vw, 24px);
  bottom: 0;
  width: min(28vw, 360px);
  max-width: 32%;
  opacity: 0.18;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin: 0 auto 18px;
  width: min(1100px, 100%);
}

.eyebrow {
  color: var(--text-secondary);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.content-header h1 {
  color: var(--text-primary);
  font-size: 30px;
  font-weight: 600;
  line-height: 1.2;
}

.title-regular {
  font-weight: 400;
}

.content-body {
  width: min(1100px, 100%);
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

@media (max-width: 900px) {
  .mobile-menu-btn {
    display: inline-flex;
  }

  .sidebar {
    position: fixed;
    width: min(264px, 86vw);
    left: 0;
    top: 0;
    align-self: auto;
  }

  .sidebar.collapsed {
    transform: translateX(-100%);
    width: min(264px, 86vw);
  }

  .main-content {
    padding: 20px;
  }

  .content-header h1 {
    font-size: 24px;
  }

  .dashboard-art {
    width: min(42vw, 240px);
    opacity: 0.12;
  }

  .user-panel.compact .user-popover {
    left: 14px;
    right: 14px;
    bottom: calc(100% + 10px);
    width: auto;
  }
}

@media (max-width: 640px) {
  .toggle-btn {
    height: 42px;
  }

  .mobile-menu-btn {
    width: 34px;
    height: 34px;
  }

  .sidebar-header {
    min-height: 94px;
    padding: 14px 12px;
  }

  .brand-logo {
    width: 160px;
    max-height: 92px;
  }

  .nav-menu {
    padding: 12px 8px;
  }

  .nav-link {
    padding: 10px 12px;
  }

  .sidebar-footer {
    padding: 12px;
  }

  .footer-buttons {
    gap: 8px;
  }

  .theme-btn,
  .logout-btn {
    min-height: 40px;
    padding: 8px;
  }

  .main-content {
    padding: 16px;
  }

  .content-header {
    margin-bottom: 14px;
  }

  .content-header h1 {
    font-size: 22px;
  }

  .dashboard-art {
    display: none;
  }
}
</style>

